/**
 * React context that owns the single {@link RealtimeClient} for the app and
 * exposes a typed `send` to descendants. Created once on mount; torn down on
 * unmount (StrictMode-safe: the effect cleans up its own socket).
 */

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ClientIntent } from '@blackjack/shared';
import { createRealtimeClient, type RealtimeClient } from './connection';

interface RealtimeContextValue {
  /** Property (not a method) so consumers can safely pass it around unbound. */
  readonly send: (intent: ClientIntent) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

export function RealtimeProvider({ children }: { readonly children: ReactNode }) {
  const clientRef = useRef<RealtimeClient | undefined>(undefined);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const client = createRealtimeClient();
    clientRef.current = client;
    setReady(true);
    return () => {
      client.close();
      clientRef.current = undefined;
      setReady(false);
    };
  }, []);

  const value: RealtimeContextValue = {
    send: (intent) => clientRef.current?.send(intent),
  };

  // `ready` keeps the provider from sending before the socket exists; children
  // render regardless (they react to the store's connection status).
  void ready;

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/** Typed sender hook. Throws if used outside the provider (developer error). */
export function useSend(): (intent: ClientIntent) => void {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useSend must be used within <RealtimeProvider>');
  return ctx.send;
}
